import * as React from "react";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import { Button, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { AspectRatio, DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { useDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { baseThemeOption } from "../baseTheme";
import { useActivity } from "../hooks/useActivity";
import { useDesignPackageMutations } from "../hooks/useDesignPackageMutations";
import { useEditorThemes } from "../hooks/useEditorThemes";
import { usePreviews } from "../hooks/usePreviews";
import { slideStateFor } from "../slideState";
import { buildThread } from "../thread";
import { AllThemesGrid } from "./AllThemesGrid";
import { CandidateCompare } from "./CandidateCompare";
import { inFlightReasonFor } from "./DisabledReason";
import { EditorMode } from "./EditorHeader";
import { FitFrame } from "./FitFrame";
import { SlidePreview } from "./SlidePreview";
import { SlideRail } from "./SlideRail";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { useViewer } from "../viewer";

// Widths that keep the large view within 60vh of height at each aspect ratio.
const LARGE_MAX_WIDTH: Record<AspectRatio, string> = {
  square_1_1: "max-w-[60vh]",
  portrait_4_5: "max-w-[48vh]",
  portrait_9_16: "max-w-[33.75vh]",
};

interface PreviewPanelProps {
  pkg: DesignPackageDetail;
  selectedItemId: number;
  onSelectItem: (id: number) => void;
  themeId: number | null;
  onThemeChange: (id: number | null) => void;
  mode: EditorMode;
  onOpenHelp(topic: "how" | "ask" | "backgrounds"): void;
  /** The selected slide's own tools, inside its card under the large view (the backgrounds tray). */
  children?: React.ReactNode;
}

export const PreviewPanel: React.FC<PreviewPanelProps> = ({
  pkg,
  selectedItemId,
  onSelectItem,
  themeId,
  onThemeChange,
  mode,
  children,
}) => {
  const access = useDynamicPromptsAccess();
  const viewer = useViewer();
  const { approve, discard, refine, busy } = useDesignPackageMutations(pkg.id);
  const { set, slideFor, ensureRendered, renderState, retry, triedSlide } = usePreviews(pkg.id, themeId);
  const { options } = useEditorThemes();
  const { events } = useActivity(pkg.id);
  const [allThemesOpen, setAllThemesOpen] = React.useState(false);

  const items = React.useMemo(() => [...pkg.items].sort((a, b) => a.position - b.position), [pkg.items]);
  const selected = items.find(i => i.id === selectedItemId) ?? items[0];
  const canEdit = mode === "owner" && access.canEdit;
  // Keep and Discard would be refused with refine_in_progress while any refinement runs.
  const inFlightReason = pkg.inFlight?.kind === "refine" ? inFlightReasonFor(pkg.inFlight, viewer) : null;
  const themeName = options.find(o => o.theme.id === themeId)?.theme.name;
  // How the base preview (no theme) is branded, for copy that says what an unthemed preview shows.
  const baseBranding = baseThemeOption(options, pkg.coachId) ? "in your default theme" : "with standard branding";

  const slideStates = set?.slides.map(s => `${s.designPackageItemId}:${s.status}`).join(",");
  React.useEffect(() => {
    ensureRendered();
  }, [slideStates, ensureRendered]);

  if (!selected) return null;

  const multiSlide = items.length > 1;
  const selectedIndex = items.findIndex(i => i.id === selected.id);
  const previousItem = items[selectedIndex - 1];
  const nextItem = items[selectedIndex + 1];
  const stageTitle = `Slide ${selected.position} of ${items.length}`;

  const selectedState = slideStateFor(selected, busy);
  const selectedImage = slideFor(selected.id);
  // A background being tried replaces the large view once its sample renders; until then the
  // current image stays under the updating overlay.
  const tried = triedSlide(selected.id);
  const largeImage = !tried
    ? selectedImage
    : tried.imageUrl
    ? { url: tried.imageUrl, state: "ready" as const }
    : { url: selectedImage.url, state: selectedImage.url ? ("updating" as const) : ("missing" as const) };

  const retryCandidate = () => {
    const request = buildThread(events, pkg)
      // An admin's code edit has no instructions to run again.
      .filter(entry => entry.kind === "request" && entry.source !== "html_edit")
      .filter(entry => entry.kind === "request" && (entry.scope === "all" || entry.scope.itemId === selected.id))
      .pop();
    if (request && request.kind === "request") {
      refine.mutate({ instructions: request.instructions, itemId: selected.id, baseDesignTemplateId: null });
    }
  };

  const renderLarge = () => {
    if (selected.candidateTemplate) {
      return (
        <CandidateCompare
          item={selected}
          aspectRatio={pkg.aspectRatio}
          beforeUrl={selected.activeTemplate.previewUrl}
          afterUrl={selected.candidateTemplate.previewUrl}
          afterState={
            selectedState === "candidate_rendering"
              ? "rendering"
              : selectedState === "candidate_failed"
              ? "failed"
              : "ready"
          }
          busy={busy[selected.id] ?? null}
          canEdit={canEdit}
          isPublished={!!pkg.publishedAt}
          onKeep={() => approve.mutate(selected.id)}
          onDiscard={() => discard.mutate(selected.id)}
          onRetry={retryCandidate}
          disabledReason={inFlightReason}
          baseBranding={baseBranding}
        />
      );
    }
    return (
      // On wide screens the slide takes the height left in its card, so the column never scrolls.
      <FitFrame aspectRatio={pkg.aspectRatio} className={cn("mx-auto", LARGE_MAX_WIDTH[pkg.aspectRatio])}>
        <SlidePreview
          item={selected}
          aspectRatio={pkg.aspectRatio}
          imageUrl={largeImage.url}
          imageState={largeImage.state}
          state={selectedState}
          size="large"
          loadingLabel={renderState === "rendering" && themeName ? `Rendering with ${themeName}…` : undefined}
        />
      </FitFrame>
    );
  };

  return (
    <div className="flex flex-col gap-4 lg:h-full lg:min-h-0" data-testid="preview-panel">
      <div className={cn("flex flex-col lg:min-h-0 lg:flex-1")}>
        <section
          // The slide column sits on the page, left of the stage: one rounded grey panel holding the
          // slide's title, its tools, and the slide.
          className={cn(
            "grid min-w-0 grid-cols-1 gap-2 lg:min-h-0 lg:flex-1 lg:grid-rows-[minmax(0,1fr)] lg:gap-0",
            multiSlide && "lg:grid-cols-[10rem_minmax(0,1fr)]"
          )}
          aria-labelledby="selected-slide-title"
          data-testid="slide-stage"
        >
          {multiSlide && (
            <SlideRail
              items={items}
              selectedItemId={selected.id}
              onSelect={onSelectItem}
              aspectRatio={pkg.aspectRatio}
              imageFor={slideFor}
              stateFor={item => slideStateFor(item, busy)}
            />
          )}
          <div
            className={cn(
              "flex min-w-0 flex-col rounded-2xl border border-slate-200 bg-slate-50 shadow-sm lg:min-h-0",
              // A soft shadow falling left onto the slide column, so the stage sits above it.
              multiSlide &&
                "lg:col-start-2 lg:shadow-[-6px_0_14px_-8px_rgb(15_23_42/0.14),0_1px_2px_rgb(15_23_42/0.05)]"
            )}
            data-testid="slide-stage-panel"
          >
            <div
              className="flex min-h-12 min-w-0 shrink-0 items-center gap-2 px-4 pt-2"
              data-testid="slide-stage-header"
            >
              <div
                id="selected-slide-title"
                className="min-w-0 truncate text-sm font-semibold tabular-nums text-slate-800"
              >
                {multiSlide ? stageTitle : "Your design"}
              </div>
              {multiSlide && (
                <div className="flex shrink-0 items-center">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Previous slide"
                    disabled={!previousItem}
                    onClick={() => previousItem && onSelectItem(previousItem.id)}
                  >
                    <ChevronLeft aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Next slide"
                    disabled={!nextItem}
                    onClick={() => nextItem && onSelectItem(nextItem.id)}
                  >
                    <ChevronRight aria-hidden="true" />
                  </Button>
                </div>
              )}
              <div className="ml-auto flex items-center gap-1">
                {selected.candidateTemplate ? (
                  // Both versions are rendered without a theme; the coach's choice comes back after Keep or Discard.
                  <Tooltip>
                    <Tooltip.Trigger render={<span className="inline-flex" />}>
                      <ThemeSwitcher value={null} coachId={pkg.coachId} onChange={onThemeChange} disabled />
                    </Tooltip.Trigger>
                    <Tooltip.Content className="max-w-64">
                      Both versions are shown {baseBranding}. Keep or discard the new version to preview other themes.
                    </Tooltip.Content>
                  </Tooltip>
                ) : (
                  <ThemeSwitcher
                    value={themeId}
                    coachId={pkg.coachId}
                    onChange={onThemeChange}
                    onCompareAll={() => setAllThemesOpen(true)}
                  />
                )}
                <Tooltip>
                  <Tooltip.Trigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="About preview data"
                        className="text-slate-400"
                      />
                    }
                  >
                    <Info aria-hidden="true" />
                  </Tooltip.Trigger>
                  <Tooltip.Content className="max-w-64">
                    Previews use sample reviews and a sample player. Use "Post for a player" to see it with real
                    reviews.
                  </Tooltip.Content>
                </Tooltip>
              </div>
            </div>
            <div className="flex min-w-0 flex-col gap-4 px-4 pt-2 pb-4 lg:min-h-0 lg:flex-1">
              {renderState === "failed" && !selected.candidateTemplate && (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-destructive">We couldn't render this preview.</p>
                  <Button variant="link" size="inline" onClick={retry}>
                    Try again
                  </Button>
                </div>
              )}

              <div className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:items-center lg:justify-center lg:overflow-hidden">
                {renderLarge()}
              </div>

              {children}
            </div>
          </div>
        </section>
      </div>

      <AllThemesGrid
        open={allThemesOpen}
        onOpenChange={setAllThemesOpen}
        pkg={pkg}
        selectedItemId={selected.id}
        currentThemeId={themeId}
      />
    </div>
  );
};
